import React, {
  useState,
  useContext,
  memo,
  Fragment,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Accordion,
  useAccordionButton,
  AccordionContext,
} from "react-bootstrap";
import { getUser } from "../../../../utilities/setting";
import { USER_ROLES, ROUTES } from "../../../../utilities/constant";
import DashboardIcon from "@mui/icons-material/Dashboard";
import UserListIcon from "@mui/icons-material/RecentActors";
import CampaignIcon from "@mui/icons-material/Campaign";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import RealtimeIcon from "@mui/icons-material/Update";
import RemarkIcon from "@mui/icons-material/RateReview";
import GroupsIcon from "@mui/icons-material/Groups";
import DialpadIcon from "@mui/icons-material/Dialpad";
import LocationIcon from "@mui/icons-material/LocationOn";
import BreakIcon from "@mui/icons-material/ExitToApp";
import CreditHistoryIcon from "@mui/icons-material/CreditScore";
import ReportsIcon from "@mui/icons-material/Description";
import VoiceLibraryIcon from "@mui/icons-material/VoiceChat";
import ActivityListIcon from "@mui/icons-material/ChecklistRtl";
import VoicePlanIcon from "@mui/icons-material/InterpreterMode";
import ModuleIcon from "@mui/icons-material/AppsOutage";
import NumberIcon from "@mui/icons-material/Tty";
import DataCenterIcon from "@mui/icons-material/LaptopMac";
import ServerManagementIcon from "@mui/icons-material/Router";
import TrunksIcon from "@mui/icons-material/Dialpad";
import ACLSettingIcon from "@mui/icons-material/FormatLineSpacing";
import CompanyIcon from "@mui/icons-material/Business";
import CallcenterListIcon from "@mui/icons-material/ListAlt";
import ExtensionIcon from "@mui/icons-material/Extension";
import ManageAgentIcon from "@mui/icons-material/SupportAgent";
import IVRIcon from "@mui/icons-material/PermPhoneMsg";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SmsIcon from "@mui/icons-material/Sms";
import MessageIcon from "@mui/icons-material/Message";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

// ─── Sidebar Search ──────────────────────────────────────────────────────────
const SidebarSearch = memo(
  ({ userRole, moduleOperations, contentId, loggedInUser, activeModule }) => {
    const [query, setQuery] = useState("");
    const [showResults, setShowResults] = useState(false);
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const navigate = useNavigate();

    // Validation: only allow letters + space (space not allowed at start)
    const handleInputChange = (e) => {
      const val = e.target.value;
      // Allow empty string (clearing)
      if (val === "") {
        setQuery("");
        setShowResults(false);
        return;
      }
      // Must start with a letter, then letters/spaces only
      if (/^[a-zA-Z][a-zA-Z ]*$/.test(val) || /^[a-zA-Z]$/.test(val)) {
        setQuery(val);
        setShowResults(true);
      } else if (/^[a-zA-Z][a-zA-Z ]*$/.test(val.trimEnd())) {
        // allow trailing space only if already has text
        setQuery(val);
        setShowResults(true);
      }
    };

    const clearSearch = () => {
      setQuery("");
      setShowResults(false);
      inputRef.current?.focus();
    };

    // Close results when clicking outside
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          setShowResults(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Build a flat list of all nav items (respecting permissions)
    const allNavItems = useCallback(() => {
      const items = [];

      const isSuperAdmin = userRole === USER_ROLES.SUPERADMIN;
      const mod = moduleOperations || {};

      const canView = (moduleName) =>
        isSuperAdmin ||
        (mod[moduleName]?.read && mod[moduleName]?.view_permission);

      // Dashboard
      items.push({
        label: "Dashboard",
        path: `/${ROUTES.DASHBOARD}`,
        icon: <DashboardIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
      });

      // Users
      if (canView("Users")) {
        items.push({
          label: "Users",
          path: `/${ROUTES.USERS.LIST}`,
          icon: <UserListIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Company List
      if (canView("Company List")) {
        items.push({
          label: "Company List",
          path: `/${ROUTES.COMPANIES.LIST}`,
          icon: <CompanyIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Licence History
      if (isSuperAdmin) {
        items.push({
          label: "Licence History",
          path: `/${ROUTES.LICENCE_HISTORY.LIST}`,
          icon: <CallcenterListIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Trunks
      if (canView("TRUNKS")) {
        items.push({
          label: "Trunks",
          path: `/${ROUTES.TRUNKS.LIST}`,
          icon: <TrunksIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Campaigns
      if (canView("Campaigns")) {
        items.push({
          label: "Outbound Campaigns",
          path: `/${ROUTES.CAMPAIGNS.LIST}`,
          icon: <CampaignIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Contacts
      if (isSuperAdmin || canView("Contact Group")) {
        items.push({
          label: "Contacts > Contact Group",
          path: `/${ROUTES.CONTACTGROUP.LIST}`,
          icon: <ContactPageIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Contacts",
        });
      }
      if (isSuperAdmin || canView("All Contacts")) {
        items.push({
          label: "Contacts > All Contacts",
          path: `/${ROUTES.CONTACTGROUP.ALL_CONTACTS}`,
          icon: <ContactPageIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Contacts",
        });
      }

      // Breaks
      if (canView("Breaks")) {
        items.push({
          label: "Breaks",
          path: `/${ROUTES.BREAKS.LIST}`,
          icon: <BreakIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Credit History
      if (canView("Credit History")) {
        items.push({
          label: "Credit History",
          path: `/${ROUTES.CREDIT_HISTORY}`,
          icon: <CreditHistoryIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Numbers
      if (canView("Numbers")) {
        items.push({
          label: "Numbers > Numbers",
          path: `/${ROUTES.NUMBERS.LIST}`,
          icon: <NumberIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Numbers",
        });
        if (isSuperAdmin) {
          items.push({
            label: "Numbers > Numbers Files List",
            path: `/${ROUTES.NUMBERS.NUMBER_FILE_LIST}`,
            icon: <NumberIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
            parent: "Numbers",
          });
          items.push({
            label: "Numbers > Status for approval",
            path: `/${ROUTES.NUMBERS.STATUS_CHANGE_ACTION}`,
            icon: <NumberIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
            parent: "Numbers",
          });
          items.push({
            label: "Numbers > Historical DIDs",
            path: `/${ROUTES.NUMBERS.DID_HISTORY}`,
            icon: <NumberIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
            parent: "Numbers",
          });
        }
      }

      // Call Centres
      if (
        canView("Call Centres List") ||
        loggedInUser?.assignedCompany?.is_own_company
      ) {
        items.push({
          label: "Call Centres List",
          path: `/${ROUTES.CALL_CENTRES.LIST}`,
          icon: <CallcenterListIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // IVR
      if (activeModule?.includes("IVR")) {
        items.push({
          label: "IVR > IVR List",
          path: `/${ROUTES.IVR.LIST.replace(":cid", contentId)}`,
          icon: <IVRIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "IVR",
        });
      }
      if (activeModule?.includes("Voice Library")) {
        items.push({
          label: "IVR > Voice Library > Add Voice Files",
          path: `/${ROUTES.IVR.ADD_VOICE}`,
          icon: <VoiceLibraryIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "IVR > Voice Library",
        });
        items.push({
          label: "IVR > Voice Library > View Voice Files",
          path: `/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`,
          icon: <VoiceLibraryIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "IVR > Voice Library",
        });
      }
      if (activeModule?.includes("TTS")) {
        items.push({
          label: "IVR > TTS > Add TTS",
          path: `/${ROUTES.TTS.CREATE}`,
          icon: <VoiceLibraryIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "IVR > TTS",
        });
        items.push({
          label: "IVR > TTS > View TTS List",
          path: `/${ROUTES.TTS.LIST}`,
          icon: <VoiceLibraryIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "IVR > TTS",
        });
      }

      // Groups
      if (canView("Groups")) {
        items.push({
          label: "Groups > Groups List",
          path: `/${ROUTES.MANAGE_GROUPS.GROUP_LIST}`,
          icon: <GroupsIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Groups",
        });
        items.push({
          label: "Groups > Bulk Group Add",
          path: `/${ROUTES.MANAGE_GROUPS.BULK_ADD}`,
          icon: <GroupsIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Groups",
        });
        items.push({
          label: "Groups > Bulk Group Delete",
          path: `/${ROUTES.MANAGE_GROUPS.BULK_DELETE}`,
          icon: <GroupsIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Groups",
        });
      }

      // Locations
      if (canView("Locations")) {
        items.push({
          label: "Locations > Location List",
          path: `/${ROUTES.MANAGE_LOCATIONS.LOCATION_LIST}`,
          icon: <LocationIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Locations",
        });
        items.push({
          label: "Locations > Bulk Location Add",
          path: `/${ROUTES.MANAGE_LOCATIONS.BULK_ADD}`,
          icon: <LocationIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Locations",
        });
        items.push({
          label: "Locations > Bulk Location Delete",
          path: `/${ROUTES.MANAGE_LOCATIONS.BULK_DELETE}`,
          icon: <LocationIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Locations",
        });
      }

      // PBX Management
      if (canView("PBX Management")) {
        items.push({
          label: "PBX Management",
          path: `/${ROUTES.MANAGE_DIALERS.DIALER_LISTING}`,
          icon: <DialpadIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Reports
      if (canView("Incoming Summary")) {
        items.push({
          label: "Reports > Incoming Summary",
          path: `/${ROUTES.IVR.INCOMING_SUMMARY}`,
          icon: <ReportsIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Reports",
        });
      }
      if (isSuperAdmin || canView("Download Report")) {
        items.push({
          label: "Reports > Download Reports",
          path: `/${ROUTES.IVR.DOWNLOAD_REPORT}`,
          icon: <ReportsIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Reports",
        });
      }

      // Data Center
      if (canView("Data Center")) {
        items.push({
          label: "Data Center",
          path: `/${ROUTES.DATA_CENTER.LIST}`,
          icon: <DataCenterIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // SMS Webhooks
      if (canView("SMS Webhook")) {
        items.push({
          label: "SMS Webhooks",
          path: `/${ROUTES.SMSWEBHOOK.LIST}`,
          icon: <SmsIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Manage Webhooks
      if (canView("Webhook")) {
        items.push({
          label: "Manage Webhooks",
          path: `/${ROUTES.MANAGE_WEBHOOK.LIST}`,
          icon: <SyncAltIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Manage Prompts
      if (canView("Manage Prompts")) {
        items.push({
          label: "Manage Prompts",
          path: `/${ROUTES.VOICE_LIBRARY.MANAGE_PROMPTS}`,
          icon: <VoiceLibraryIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Gateway Management
      if (canView("Gateway Management")) {
        items.push({
          label: "Gateway Management",
          path: `/${ROUTES.SERVER_MANAGEMENT.LIST}`,
          icon: (
            <ServerManagementIcon sx={{ fontSize: 18, color: "#7c7fff" }} />
          ),
        });
      }

      // Module
      if (canView("Module List")) {
        items.push({
          label: "Module > Module List",
          path: `/${ROUTES.MODULE.LIST}`,
          icon: <ModuleIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Module",
        });
      }
      if (canView("ACL Setting List")) {
        items.push({
          label: "Module > ACL Setting List",
          path: `/${ROUTES.ACL_SETTING.LIST}`,
          icon: <ModuleIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Module",
        });
      }

      // Manage Agents
      if (canView("Agents")) {
        items.push({
          label: "Manage Agents > Agents",
          path: `/${ROUTES.MANAGE_AGENTS.AGENTS}`,
          icon: <ManageAgentIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Manage Agents",
        });
      }
      if (canView("Call Group")) {
        items.push({
          label: "Manage Agents > Call Group",
          path: `/${ROUTES.MANAGE_AGENTS.AGENT_GROUP_LIST}`,
          icon: <ManageAgentIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
          parent: "Manage Agents",
        });
      }

      // Extension
      if (canView("Extension")) {
        items.push({
          label: "Extension",
          path: `/${ROUTES.MANAGE_AGENTS.EXTENSION}`,
          icon: <ExtensionIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Agent file list
      if (isSuperAdmin) {
        items.push({
          label: "Agent File List",
          path: `/${ROUTES.MANAGE_AGENTS.AGENT_FILE_LIST}`,
          icon: <CallcenterListIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Activity List
      if (canView("Activity List")) {
        items.push({
          label: "Activity List",
          path: `/${ROUTES.JOURNEY_OPERATION.LIST}`,
          icon: <ActivityListIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Voice Plans
      if (canView("Voice Plans")) {
        items.push({
          label: "Voice Plans",
          path: `/${ROUTES.VOICE_PLAN.LIST}`,
          icon: <VoicePlanIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Flash Message
      if (canView("Flash Message")) {
        items.push({
          label: "Flash Message",
          path: `/${ROUTES.FLASH_MESSAGE.LIST}`,
          icon: <MessageIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // TTS Plans
      if (canView("TTS Plans")) {
        items.push({
          label: "TTS Plans",
          path: `/${ROUTES.TTSPLANS.LIST}`,
          icon: <VoicePlanIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Realtime
      if (canView("Realtime")) {
        items.push({
          label: "Realtime",
          path: `/${ROUTES.REALTIME.LIST}`,
          icon: <RealtimeIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      // Remarks Status
      if (canView("Remarks Status")) {
        items.push({
          label: "Remarks Status",
          path: `/${ROUTES.REMARKS.LIST}`,
          icon: <RemarkIcon sx={{ fontSize: 18, color: "#7c7fff" }} />,
        });
      }

      return items;
    }, [userRole, moduleOperations, contentId, loggedInUser, activeModule]);

    const trimmedQuery = query.trim();
    const filteredItems =
      trimmedQuery.length > 0
        ? allNavItems().filter((item) =>
            item.label.toLowerCase().includes(trimmedQuery.toLowerCase()),
          )
        : [];

    const highlightText = (text, search) => {
      if (!search) return text;
      const regex = new RegExp(
        `(${search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
        "gi",
      );
      const parts = text.split(regex);
      return parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            style={{
              background: "rgba(124,127,255,0.35)",
              color: "#fff",
              borderRadius: "2px",
              padding: "0 1px",
            }}
          >
            {part}
          </mark>
        ) : (
          part
        ),
      );
    };

    const handleResultClick = (path) => {
      navigate(path);
      setQuery("");
      setShowResults(false);
    };

    return (
      <li
        className="nav-item"
        style={{ padding: "10px 14px 6px", position: "relative" }}
        ref={containerRef}
      >
        {/* Search Input */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            background: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.13)",
            borderRadius: "8px",
            padding: "6px 10px",
            gap: "8px",
            transition: "border-color 0.2s, box-shadow 0.2s",
            marginTop: "-15px",
            marginBottom: "10px",
            boxShadow:
              showResults && trimmedQuery
                ? "0 0 0 2px rgba(124,127,255,0.3)"
                : "none",
          }}
        >
          <SearchIcon
            sx={{
              fontSize: 17,
              color: "rgb(255, 255, 255)",
              flexShrink: 0,
            }}
          />
          <input
            className="search-input"
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => {
              if (trimmedQuery) setShowResults(true);
            }}
            placeholder="Search anything..."
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#fff",
              fontSize: "13px",
              flex: 1,
              minWidth: 0,
              letterSpacing: "0.2px",
            }}
          />
          {query && (
            <button
              onClick={clearSearch}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
                color: "rgb(255, 255, 255)",
                flexShrink: 0,
              }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </button>
          )}
        </div>

        {/* Results Dropdown */}
        {showResults && trimmedQuery && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% - 2px)",
              left: "14px",
              right: "14px",
              // background: "linear-gradient(145deg, #0f1265 0%, #060912 100%)",
              background: "#060912",
              border: "1px solid rgba(124,127,255,0.25)",
              borderRadius: "10px",
              boxShadow:
                "0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(124,127,255,0.1)",
              zIndex: 9999,
              maxHeight: "340px",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {filteredItems.length === 0 ? (
              <div
                style={{
                  padding: "18px 14px",
                  textAlign: "center",
                  color: "rgb(255, 255, 255)",
                  fontSize: "13px",
                  fontStyle: "italic",
                }}
              >
                No results for "{trimmedQuery}"
              </div>
            ) : (
              <>
                <div
                  style={{
                    padding: "7px 12px 5px",
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.8px",
                    textTransform: "uppercase",
                    color: "rgba(255, 255, 255, 0.84)",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  {filteredItems.length} result
                  {filteredItems.length !== 1 ? "s" : ""}
                </div>
                {filteredItems.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleResultClick(item.path)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "9px 12px",
                      cursor: "pointer",
                      borderBottom:
                        idx < filteredItems.length - 1
                          ? "1px solid rgba(255,255,255,0.04)"
                          : "none",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background =
                        "rgba(124,127,255,0.12)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {item.icon}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      {item.parent && (
                        <div
                          style={{
                            fontSize: "10px",
                            color: "rgba(255, 255, 255, 0.83)",
                            marginBottom: "1px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {item.parent}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#e8e8ff",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {highlightText(
                          item.label.includes(">")
                            ? item.label.split("> ").pop()
                            : item.label,
                          trimmedQuery,
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Scrollbar styling */}
            <style>{`
            div[data-search-results]::-webkit-scrollbar { width: 4px; }
            div[data-search-results]::-webkit-scrollbar-track { background: transparent; }
            div[data-search-results]::-webkit-scrollbar-thumb { background: rgba(124,127,255,0.3); border-radius: 2px; }
          `}</style>
          </div>
        )}
      </li>
    );
  },
);

// ─── CustomToggle ─────────────────────────────────────────────────────────────
function CustomToggle({ children, eventKey, onClick, activeMenu }) {
  const { activeEventKey } = useContext(AccordionContext);
  const isExpanded = activeEventKey === eventKey;

  const decoratedOnClick = useAccordionButton(eventKey, (active) =>
    onClick({ state: !active, eventKey: eventKey }),
  );

  return (
    <Link
      to="#"
      aria-expanded={isExpanded ? "true" : "false"}
      className={`nav-link ${isExpanded ? "active" : ""}`}
      role="button"
      onClick={(e) => {
        decoratedOnClick();
      }}
    >
      {children}
    </Link>
  );
}

// ─── VerticalNavWithSidebar ──────────────────────────────────────────────────────────────
const VerticalNavWithSidebar = memo((props) => {
  const [activeMenu, setActiveMenu] = useState(false);
  const [active, setActive] = useState("");
  const [activeAccordionKey, setActiveAccordionKey] = useState(null);
  const [contentId, setContentId] = useState(undefined);
  const [userRole, setUserRole] = useState(undefined);
  const [activeModule, setActiveModules] = useState([]);
  const [moduleOperations, setModuleOperations] = useState({});
  const [loggedInUser, setLoggedInUser] = useState(null);
  let location = useLocation();

  useEffect(() => {
    getUser().then((resp) => {
      if (resp) {
        setLoggedInUser(resp);
        setUserRole(resp.role);
        setContentId(resp.id);
        if (resp?.acl_settings) {
          setModuleOperations(JSON.parse(resp.acl_settings.module_operations));
          const modules = getKeysWithTrueValues(
            JSON.parse(resp.acl_settings.module_operations),
          );
          setActiveModules(modules);
        }
      }
    });
  }, []);

  function getKeysWithTrueValues(moduleOperations) {
    return Object.keys(moduleOperations).filter((key) => {
      const permissions = moduleOperations[key];
      return Object.values(permissions).some((value) => value === true);
    });
  }

  const clearActiveStates = (
    exceptAccordionKey = null,
    newActiveValue = null,
  ) => {
    if (newActiveValue === null) {
      setActive("");
    }
    setActiveMenu(false);
    if (exceptAccordionKey !== null) {
      if (activeAccordionKey !== exceptAccordionKey) {
        setActiveAccordionKey(exceptAccordionKey);
      }
    } else {
      if (activeAccordionKey !== null) {
        setActiveAccordionKey(null);
      }
    }
  };

  return (
    <Fragment>
      <Accordion
        as="ul"
        className="navbar-nav iq-main-menu"
        activeKey={activeAccordionKey}
        onSelect={(eventKey) => {
          const newKey = activeAccordionKey === eventKey ? null : eventKey;
          setActiveAccordionKey(newKey);
          if (newKey === null) {
            setActive("");
            setActiveMenu(false);
          } else {
            clearActiveStates(newKey);
          }
        }}
      >
        <li className="nav-item static-item">
          <Link
            className="nav-link static-item disabled"
            to="#"
            tabIndex="-1"
          ></Link>
        </li>

        {/* ── Search Bar ── */}
        <SidebarSearch
          userRole={userRole}
          moduleOperations={moduleOperations}
          contentId={contentId}
          loggedInUser={loggedInUser}
          activeModule={activeModule}
        />

        {/* ── Dashboard ── */}
        <li
        style={{ marginBottom: "-10px" }}
          className={`${
            location.pathname === `/${ROUTES.DASHBOARD}` ? "active" : ""
          } nav-item `}
        >
          <Link
            className={`${
              location.pathname === `/${ROUTES.DASHBOARD}` ? "active" : ""
            } nav-link `}
            aria-current="page"
            to={`/${ROUTES.DASHBOARD}`}
            onClick={() => {
              setActive("");
              setActiveAccordionKey(null);
              setActiveMenu(false);
            }}
          >
            <DashboardIcon sx={{ fontSize: 22, color: "white" }} />
            <span className="item-name">Dashboard</span>
          </Link>
        </li>
        <li>
          <hr className="hr-horizontal" />
        </li>

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.Users?.read &&
            moduleOperations?.Users?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-user"
            bsPrefix={`nav-item ${active === "user" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "user");
              setActive("user");
            }}
          >
            <Link
              className={`${
                location.pathname === `/${ROUTES.USERS.LIST}` ? "active" : ""
              } nav-link`}
              to={`/${ROUTES.USERS.LIST}`}
            >
              <UserListIcon sx={{ fontSize: 25, color: "white" }} />
              <i className="sidenav-mini-icon"> U </i>
              <span className="item-name">Users</span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Company List"]?.read &&
            moduleOperations?.["Company List"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-company-list"
            bsPrefix={`nav-item ${active === "company-list" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "company-list");
              setActive("company-list");
            }}
          >
            <Link
              className={`${
                location.pathname === `/${ROUTES.COMPANIES.LIST}`
                  ? "active"
                  : ""
              } nav-link`}
              to={`/${ROUTES.COMPANIES.LIST}`}
            >
              <CompanyIcon sx={{ fontSize: 22, color: "white" }} />
              <span style={{ marginLeft: "15px" }} className="item-name">
                Company List
              </span>
            </Link>
          </Accordion.Item>
        )}

        <Accordion.Item
          hidden={userRole !== USER_ROLES.SUPERADMIN}
          as="li"
          eventKey="sidebar-licence-history-list"
          bsPrefix={`nav-item ${active === "licence-history" ? "active" : ""} `}
          onClick={() => {
            clearActiveStates(null, "licence-history");
            setActive("licence-history");
          }}
        >
          <Link
            className={`${
              location.pathname === `/${ROUTES.LICENCE_HISTORY.LIST}`
                ? "active"
                : ""
            } nav-link`}
            to={`/${ROUTES.LICENCE_HISTORY.LIST}`}
          >
            <CallcenterListIcon sx={{ fontSize: 22, color: "white" }} />
            <span style={{ marginLeft: "15px" }} className="item-name">
              Licence History
            </span>
          </Link>
        </Accordion.Item>

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.TRUNKS?.read &&
            moduleOperations?.TRUNKS?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-trunks"
            bsPrefix={`nav-item ${active === "trunks" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "trunks");
              setActive("trunks");
            }}
          >
            <Link
              className={`${
                location.pathname === `/${ROUTES.TRUNKS.LIST}` ? "active" : ""
              } nav-link`}
              to={`/${ROUTES.TRUNKS.LIST}`}
            >
              <TrunksIcon sx={{ fontSize: 22, color: "white" }} />
              <i className="sidenav-mini-icon"> U </i>
              <span className="item-name">Trunks</span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.Campaigns?.read &&
            moduleOperations?.Campaigns?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-campaigns"
            bsPrefix={`nav-item ${active === "campaigns" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "campaigns");
              setActive("campaigns");
            }}
          >
            <Link
              className={`${
                location.pathname === `/${ROUTES.CAMPAIGNS.LIST}`
                  ? "active"
                  : ""
              } nav-link`}
              to={`/${ROUTES.CAMPAIGNS.LIST}`}
            >
              <CampaignIcon sx={{ fontSize: 22, color: "white" }} />
              <span style={{ marginLeft: "15px" }} className="item-name">
                Outbound Campaigns
              </span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations["Contact Group"]?.read &&
            moduleOperations["Contact Group"]?.view_permission) ||
          (moduleOperations["All Contacts"]?.read &&
            moduleOperations["All Contacts"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-contactGroup"
            bsPrefix={`nav-item ${active === "contactGroup" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates("sidebar-contactGroup", "contactGroup");
              setActive("contactGroup");
            }}
          >
            <CustomToggle
              eventKey="sidebar-contactGroup"
              onClick={(activeKey) => {
                setActiveMenu(activeKey);
                if (activeAccordionKey !== activeKey.eventKey) {
                  setActiveAccordionKey(activeKey.eventKey);
                } else {
                  setActiveAccordionKey(null);
                }
              }}
              activeMenu={activeMenu}
            >
              <ContactPageIcon sx={{ fontSize: 22, color: "white" }} />
              <span className="item-name">Contacts</span>
              <i className="right-icon">
                <svg width="18" viewBox="0 0 24 24" stroke="white">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </i>
            </CustomToggle>

            <Accordion.Collapse eventKey="sidebar-contactGroup">
              <ul
                className="sub-nav"
                style={{
                  backgroundColor: "#04005a",
                }}
              >
                {(userRole === USER_ROLES.SUPERADMIN ||
                  (moduleOperations["Contact Group"]?.read &&
                    moduleOperations["Contact Group"]?.view_permission)) && (
                  <li className="nav-item">
                    <Link
                      className={`${
                        location.pathname === `/${ROUTES.CONTACTGROUP.LIST}`
                          ? "active"
                          : ""
                      } nav-link`}
                      to={`/${ROUTES.CONTACTGROUP.LIST}`}
                    >
                      <i className="icon">
                        <svg width="10" viewBox="0 0 24 24" fill="white">
                          <circle cx="12" cy="12" r="8" fill="white" />
                        </svg>
                      </i>
                      <span className="item-name">Contact Group</span>
                    </Link>
                  </li>
                )}

                {(userRole === USER_ROLES.SUPERADMIN ||
                  (moduleOperations["All Contacts"]?.read &&
                    moduleOperations["All Contacts"]?.view_permission)) && (
                  <li className="nav-item">
                    <Link
                      className={`${
                        location.pathname ===
                        `/${ROUTES.CONTACTGROUP.ALL_CONTACTS}`
                          ? "active"
                          : ""
                      } nav-link`}
                      to={`/${ROUTES.CONTACTGROUP.ALL_CONTACTS}`}
                    >
                      <i className="icon">
                        <svg width="10" viewBox="0 0 24 24" fill="white">
                          <circle cx="12" cy="12" r="8" fill="white" />
                        </svg>
                      </i>
                      <span className="item-name">All Contacts</span>
                    </Link>
                  </li>
                )}
              </ul>
            </Accordion.Collapse>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.Breaks?.read &&
            moduleOperations?.Breaks?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-breaks"
            bsPrefix={`nav-item ${active === "breaks" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "breaks");
              setActive("breaks");
            }}
          >
            <Link
              className={`${
                location.pathname === `/${ROUTES.BREAKS.LIST}` ? "active" : ""
              } nav-link`}
              to={`/${ROUTES.BREAKS.LIST}`}
            >
              <BreakIcon sx={{ fontSize: 22, color: "white" }} />
              <span style={{ marginLeft: "15px" }} className="item-name">
                Breaks
              </span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Credit History"]?.read &&
            moduleOperations?.["Credit History"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-credit-history"
            bsPrefix={`nav-item ${active === "credits" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "credits");
              setActive("credits");
            }}
          >
            <Link
              className={`${
                location.pathname === `/${ROUTES.CREDIT_HISTORY}`
                  ? "active"
                  : ""
              } nav-link`}
              to={`/${ROUTES.CREDIT_HISTORY}`}
            >
              <CreditHistoryIcon sx={{ fontSize: 22, color: "white" }} />
              <span style={{ marginLeft: "15px" }} className="item-name">
                Credit History
              </span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.Numbers?.read &&
            moduleOperations?.Numbers?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-numbers"
            bsPrefix={`nav-item ${active === "numbers" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates("sidebar-numbers", "numbers");
              setActive("numbers");
            }}
          >
            <CustomToggle
              eventKey="sidebar-numbers"
              onClick={(activeKey) => {
                setActiveMenu(activeKey);
                if (activeAccordionKey !== activeKey.eventKey) {
                  setActiveAccordionKey(activeKey.eventKey);
                } else {
                  setActiveAccordionKey(null);
                }
              }}
              activeMenu={activeMenu}
            >
              <NumberIcon sx={{ fontSize: 22, color: "white" }} />
              <span className="item-name">Numbers</span>
              <i className="right-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </i>
            </CustomToggle>
            <Accordion.Collapse eventKey="sidebar-numbers">
              <ul className="sub-nav">
                {(userRole === USER_ROLES.SUPERADMIN ||
                  (moduleOperations?.Numbers?.read &&
                    moduleOperations?.Numbers?.view_permission)) && (
                  <>
                    <li className="nav-item">
                      <Link
                        className={`${location.pathname === `/${ROUTES.NUMBERS.LIST}` ? "active" : ""} nav-link`}
                        to={`/${ROUTES.NUMBERS.LIST}`}
                      >
                        <i className="icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            viewBox="0 0 24 24"
                            fill="white"
                          >
                            <g>
                              <circle
                                cx="12"
                                cy="12"
                                r="8"
                                fill="white"
                              ></circle>
                            </g>
                          </svg>
                        </i>
                        <span className="item-name">Numbers</span>
                      </Link>
                    </li>

                    {userRole === USER_ROLES.SUPERADMIN && (
                      <li className="nav-item">
                        <Link
                          className={`${location.pathname === `/${ROUTES.NUMBERS.NUMBER_FILE_LIST}` ? "active" : ""} nav-link`}
                          to={`/${ROUTES.NUMBERS.NUMBER_FILE_LIST}`}
                        >
                          <i className="icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <g>
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="8"
                                  fill="white"
                                ></circle>
                              </g>
                            </svg>
                          </i>
                          <span className="item-name">Numbers Files List</span>
                        </Link>
                      </li>
                    )}

                    {userRole === USER_ROLES.SUPERADMIN && (
                      <li className="nav-item">
                        <Link
                          className={`${location.pathname === `/${ROUTES.NUMBERS.STATUS_CHANGE_ACTION}` ? "active" : ""} nav-link`}
                          to={`/${ROUTES.NUMBERS.STATUS_CHANGE_ACTION}`}
                        >
                          <i className="icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <g>
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="8"
                                  fill="white"
                                ></circle>
                              </g>
                            </svg>
                          </i>
                          <i className="sidenav-mini-icon"> U </i>
                          <span className="item-name">Status for approval</span>
                        </Link>
                      </li>
                    )}

                    {userRole === USER_ROLES.SUPERADMIN && (
                      <li className="nav-item">
                        <Link
                          className={`${location.pathname === `/${ROUTES.NUMBERS.DID_HISTORY}` ? "active" : ""} nav-link`}
                          to={`/${ROUTES.NUMBERS.DID_HISTORY}`}
                        >
                          <i className="icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <g>
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="8"
                                  fill="white"
                                ></circle>
                              </g>
                            </svg>
                          </i>
                          <i className="sidenav-mini-icon"> U </i>
                          <span className="item-name">Historical DIDs</span>
                        </Link>
                      </li>
                    )}
                  </>
                )}
              </ul>
            </Accordion.Collapse>
          </Accordion.Item>
        )}

        {((moduleOperations?.["Call Centres List"]?.read &&
          moduleOperations?.["Call Centres List"]?.view_permission) ||
          loggedInUser?.assignedCompany?.is_own_company) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-call-centre"
            bsPrefix={`nav-item ${active === "call-centre" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "call-centre");
              setActive("call-centre");
            }}
          >
            <ul className="sub-nav">
              <Link
                className={`${location.pathname === `/${ROUTES.CALL_CENTRES.LIST}` ? "active" : ""} nav-link`}
                to={`/${ROUTES.CALL_CENTRES.LIST}`}
              >
                <CallcenterListIcon sx={{ fontSize: 22, color: "white" }} />
                <span className="item-name">Call Centres List</span>
              </Link>
            </ul>
          </Accordion.Item>
        )}

        {((moduleOperations?.IVR?.read &&
          moduleOperations?.IVR?.view_permission) ||
          (moduleOperations?.["Voice Library"]?.read &&
            moduleOperations?.["Voice Library"]?.view_permission) ||
          (moduleOperations?.TTS?.read &&
            moduleOperations?.TTS?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-ivr"
            bsPrefix={`nav-item ${active === "ivr" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates("sidebar-ivr", "ivr");
              setActive("ivr");
            }}
          >
            <CustomToggle
              eventKey="sidebar-ivr"
              onClick={(activeKey) => {
                setActiveMenu(activeKey);
                if (activeAccordionKey !== activeKey.eventKey) {
                  setActiveAccordionKey(activeKey.eventKey);
                } else {
                  setActiveAccordionKey(null);
                }
              }}
              activeMenu={activeMenu}
            >
              <IVRIcon sx={{ fontSize: 22, color: "white" }} />
              <span className="item-name">IVR</span>
              <i className="right-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </i>
            </CustomToggle>

            <Accordion.Collapse eventKey="sidebar-ivr">
              <ul
                className="sub-nav"
                style={{
                  backgroundColor: "#04005a",
                }}
              >
                {activeModule.includes("IVR") && (
                  <li className="nav-item">
                    <Link
                      className={`${location.pathname === `/${ROUTES.IVR.LIST.replace(":cid", contentId)}` ? "active" : ""} nav-link`}
                      to={`/${ROUTES.IVR.LIST.replace(":cid", contentId)}`}
                    >
                      <i className="sidenav-mini-icon"> U </i>
                      <span className="item-name">IVR List</span>
                    </Link>
                  </li>
                )}

                {activeModule.includes("Voice Library") && (
                  <li className="nav-item">
                    <Accordion>
                      <Accordion.Item
                        eventKey="voice-library"
                        style={{
                          // Use solid dark sidebar color instead of grey/white
                          backgroundColor: "#04005a",
                        }}
                      >
                        <CustomToggle
                          eventKey="voice-library"
                          onClick={(activeKey) => setActiveMenu(activeKey)}
                          activeMenu={activeMenu}
                        >
                          <VoiceLibraryIcon
                            sx={{ fontSize: 22, color: "white" }}
                          />
                          <span className="item-name">Voice Library</span>
                          <i className="right-icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="white"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </i>
                        </CustomToggle>
                        <Accordion.Collapse eventKey="voice-library">
                          <ul
                            className="sub-nav"
                            style={{
                              // Match sidebar dark blue for Voice Library submenu background
                              backgroundColor: "#04005a",
                            }}
                          >
                            {(userRole === USER_ROLES.SUPERADMIN ||
                              moduleOperations?.["Voice Library"]?.add) && (
                              <li className="nav-item">
                                <Link
                                  className={`${location.pathname === `/${ROUTES.IVR.ADD_VOICE}` ? "active" : ""} nav-link`}
                                  to={`/${ROUTES.IVR.ADD_VOICE}`}
                                >
                                  <i className="icon">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="10"
                                      viewBox="0 0 24 24"
                                      fill="white"
                                    >
                                      <g>
                                        <circle
                                          cx="12"
                                          cy="12"
                                          r="8"
                                          fill="white"
                                        ></circle>
                                      </g>
                                    </svg>
                                  </i>
                                  <span className="item-name">
                                    Add Voice Files
                                  </span>
                                </Link>
                              </li>
                            )}
                            <li className="nav-item">
                              <Link
                                className={`${location.pathname === `/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}` ? "active" : ""} nav-link`}
                                to={`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`}
                              >
                                <i className="icon">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="10"
                                    viewBox="0 0 24 24"
                                    fill="white"
                                  >
                                    <g>
                                      <circle
                                        cx="12"
                                        cy="12"
                                        r="8"
                                        fill="white"
                                      ></circle>
                                    </g>
                                  </svg>
                                </i>
                                <span className="item-name">
                                  View Voice files
                                </span>
                              </Link>
                            </li>
                          </ul>
                        </Accordion.Collapse>
                      </Accordion.Item>
                    </Accordion>
                  </li>
                )}

                {activeModule.includes("TTS") && (
                  <li className="nav-item">
                    <Accordion>
                      <Accordion.Item
                        eventKey="tts"
                        style={{
                          backgroundColor: "#04005a",
                        }}
                      >
                        <CustomToggle
                          eventKey="tts"
                          onClick={(activeKey) => setActiveMenu(activeKey)}
                          activeMenu={activeMenu}
                        >
                          <VoiceLibraryIcon
                            sx={{ fontSize: 22, color: "white" }}
                          />
                          <span className="item-name">TTS</span>
                          <i className="right-icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="white"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </i>
                        </CustomToggle>
                        <Accordion.Collapse eventKey="tts">
                          <ul
                            className="sub-nav"
                            style={{
                              backgroundColor: "#04005a",
                            }}
                          >
                            {(userRole === USER_ROLES.SUPERADMIN ||
                              moduleOperations?.["TTS"]?.add) && (
                              <li className="nav-item">
                                <Link
                                  className={`${location.pathname === `/${ROUTES.TTS.CREATE}` ? "active" : ""} nav-link`}
                                  to={`/${ROUTES.TTS.CREATE}`}
                                >
                                  <i className="icon">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="10"
                                      viewBox="0 0 24 24"
                                      fill="white"
                                    >
                                      <g>
                                        <circle
                                          cx="12"
                                          cy="12"
                                          r="8"
                                          fill="white"
                                        ></circle>
                                      </g>
                                    </svg>
                                  </i>
                                  <span className="item-name">Add TTS </span>
                                </Link>
                              </li>
                            )}
                            <li className="nav-item">
                              <Link
                                className={`${location.pathname === `/${ROUTES.TTS.LIST}` ? "active" : ""} nav-link`}
                                to={`/${ROUTES.TTS.LIST}`}
                              >
                                <i className="icon">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="10"
                                    viewBox="0 0 24 24"
                                    fill="white"
                                  >
                                    <g>
                                      <circle
                                        cx="12"
                                        cy="12"
                                        r="8"
                                        fill="white"
                                      ></circle>
                                    </g>
                                  </svg>
                                </i>
                                <span className="item-name">View TTS List</span>
                              </Link>
                            </li>
                          </ul>
                        </Accordion.Collapse>
                      </Accordion.Item>
                    </Accordion>
                  </li>
                )}
              </ul>
            </Accordion.Collapse>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.Groups?.read &&
            moduleOperations?.Groups?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-manageGroups"
            bsPrefix={`nav-item ${active === "manageGroups" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates("sidebar-manageGroups", "manageGroups");
              setActive("manageGroups");
            }}
          >
            <CustomToggle
              eventKey="sidebar-manageGroups"
              onClick={(activeKey) => {
                setActiveMenu(activeKey);
                if (activeAccordionKey !== activeKey.eventKey) {
                  setActiveAccordionKey(activeKey.eventKey);
                } else {
                  setActiveAccordionKey(null);
                }
              }}
              activeMenu={activeMenu}
            >
              <GroupsIcon sx={{ fontSize: 22, color: "white" }} />
              <span className="item-name">Groups</span>
              <i className="right-icon">
                <svg width="18" viewBox="0 0 24 24" stroke="white">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </i>
            </CustomToggle>
            <Accordion.Collapse eventKey="sidebar-manageGroups">
              <ul
                className="sub-nav"
                style={{
                  backgroundColor: "#04005a",
                }}
              >
                <li className="nav-item">
                  <Link
                    className={`${location.pathname === `/${ROUTES.MANAGE_GROUPS.GROUP_LIST}` ? "active" : ""} nav-link`}
                    to={`/${ROUTES.MANAGE_GROUPS.GROUP_LIST}`}
                  >
                    <i className="icon">
                      <svg width="10" viewBox="0 0 24 24" fill="white">
                        <circle cx="12" cy="12" r="8" fill="white" />
                      </svg>
                    </i>
                    <span className="item-name">Groups List</span>
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={`${location.pathname === `/${ROUTES.MANAGE_GROUPS.BULK_ADD}` ? "active" : ""} nav-link`}
                    to={`/${ROUTES.MANAGE_GROUPS.BULK_ADD}`}
                  >
                    <i className="icon">
                      <svg width="10" viewBox="0 0 24 24" fill="white">
                        <circle cx="12" cy="12" r="8" fill="white" />
                      </svg>
                    </i>
                    <span className="item-name">Bulk Group Add</span>
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={`${location.pathname === `/${ROUTES.MANAGE_GROUPS.BULK_DELETE}` ? "active" : ""} nav-link`}
                    to={`/${ROUTES.MANAGE_GROUPS.BULK_DELETE}`}
                  >
                    <i className="icon">
                      <svg width="10" viewBox="0 0 24 24" fill="white">
                        <circle cx="12" cy="12" r="8" fill="white" />
                      </svg>
                    </i>
                    <span className="item-name">Bulk Group Delete</span>
                  </Link>
                </li>
              </ul>
            </Accordion.Collapse>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.Locations?.read &&
            moduleOperations?.Locations?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-manageLocations"
            bsPrefix={`nav-item ${active === "manageLocations" ? "active" : ""}`}
            onClick={() => {
              setActive("manageLocations");
              clearActiveStates("sidebar-manageLocations");
            }}
          >
            <CustomToggle
              eventKey="sidebar-manageLocations"
              onClick={(activeKey) => setActiveMenu(activeKey)}
              activeMenu={activeMenu}
            >
              <LocationIcon sx={{ fontSize: 22, color: "white" }} />
              <span className="item-name">Locations</span>
              <i className="right-icon">
                <svg width="18" viewBox="0 0 24 24" stroke="white">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </i>
            </CustomToggle>
            <Accordion.Collapse eventKey="sidebar-manageLocations">
              <ul
                className="sub-nav"
                style={{
                  backgroundColor: "#04005a",
                }}
              >
                <li className="nav-item">
                  <Link
                    className={`${location.pathname === `/${ROUTES.MANAGE_LOCATIONS.LOCATION_LIST}` ? "active" : ""} nav-link`}
                    to={`/${ROUTES.MANAGE_LOCATIONS.LOCATION_LIST}`}
                  >
                    <i className="icon">
                      <svg width="10" viewBox="0 0 24 24" fill="white">
                        <circle cx="12" cy="12" r="8" fill="white" />
                      </svg>
                    </i>
                    <span className="item-name">Location List</span>
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={`${location.pathname === `/${ROUTES.MANAGE_LOCATIONS.BULK_ADD}` ? "active" : ""} nav-link`}
                    to={`/${ROUTES.MANAGE_LOCATIONS.BULK_ADD}`}
                  >
                    <i className="icon">
                      <svg width="10" viewBox="0 0 24 24" fill="white">
                        <circle cx="12" cy="12" r="8" fill="white" />
                      </svg>
                    </i>
                    <span className="item-name">Bulk Location Add</span>
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={`${location.pathname === `/${ROUTES.MANAGE_LOCATIONS.BULK_DELETE}` ? "active" : ""} nav-link`}
                    to={`/${ROUTES.MANAGE_LOCATIONS.BULK_DELETE}`}
                  >
                    <i className="icon">
                      <svg width="10" viewBox="0 0 24 24" fill="white">
                        <circle cx="12" cy="12" r="8" fill="white" />
                      </svg>
                    </i>
                    <span className="item-name">Bulk Location Delete</span>
                  </Link>
                </li>
              </ul>
            </Accordion.Collapse>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["PBX Management"]?.read &&
            moduleOperations?.["PBX Management"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-manageDialers"
            bsPrefix={`nav-item ${active === "manageDialers" ? "active" : ""}`}
            onClick={() => {
              setActive("manageDialers");
              clearActiveStates();
            }}
          >
            <Link
              className={`${location.pathname === `/${ROUTES.MANAGE_DIALERS.DIALER_LISTING}` ? "active" : ""} nav-link`}
              to={`/${ROUTES.MANAGE_DIALERS.DIALER_LISTING}`}
            >
              <DialpadIcon sx={{ fontSize: 22, color: "white" }} />
              <span className="item-name">PBX Management</span>
            </Link>
          </Accordion.Item>
        )}

        {((moduleOperations?.["Reports"]?.read &&
          moduleOperations?.["Reports"]?.view_permission) ||
          (moduleOperations?.["Incoming Summary"]?.read &&
            moduleOperations?.["Incoming Summary"]?.view_permission) ||
          userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Download Report"]?.read &&
            moduleOperations?.["Download Report"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-reports"
            bsPrefix={`nav-item ${active === "reports" ? "active" : ""}`}
            onClick={() => {
              clearActiveStates("sidebar-reports", "reports");
              setActive("reports");
            }}
          >
            <CustomToggle
              eventKey="sidebar-reports"
              onClick={(activeKey) => {
                setActiveMenu(activeKey);
                if (activeAccordionKey !== activeKey.eventKey) {
                  setActiveAccordionKey(activeKey.eventKey);
                } else {
                  setActiveAccordionKey(null);
                }
              }}
              activeMenu={activeMenu}
            >
              <ReportsIcon sx={{ fontSize: 22, color: "white" }} />
              <span className="item-name">Reports</span>
              <i className="right-icon">
                <svg width="18" viewBox="0 0 24 24" stroke="white">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </i>
            </CustomToggle>
            <Accordion.Collapse eventKey="sidebar-reports">
              <ul
                className="sub-nav"
                style={{
                  backgroundColor: "#04005a",
                }}
              >
                {moduleOperations?.["Incoming Summary"]?.read &&
                  moduleOperations?.["Incoming Summary"]?.view_permission && (
                    <li className="nav-item">
                      <Link
                        className={`${location.pathname === `/${ROUTES.IVR.INCOMING_SUMMARY}` ? "active" : ""} nav-link`}
                        to={`/${ROUTES.IVR.INCOMING_SUMMARY}`}
                      >
                        <i className="icon">
                          <svg width="10" viewBox="0 0 24 24" fill="white">
                            <circle cx="12" cy="12" r="8" fill="white" />
                          </svg>
                        </i>
                        <span className="item-name">Incoming Summary</span>
                      </Link>
                    </li>
                  )}
                {(userRole === USER_ROLES.SUPERADMIN ||
                  (moduleOperations?.["Download Report"]?.read &&
                    moduleOperations?.["Download Report"]
                      ?.view_permission)) && (
                  <li className="nav-item">
                    <Link
                      className={`${location.pathname === `/${ROUTES.IVR.DOWNLOAD_REPORT}` ? "active" : ""} nav-link`}
                      to={`/${ROUTES.IVR.DOWNLOAD_REPORT}`}
                    >
                      <i className="icon">
                        <svg width="10" viewBox="0 0 24 24" fill="white">
                          <circle cx="12" cy="12" r="8" fill="white" />
                        </svg>
                      </i>
                      <span className="item-name">Download Reports</span>
                    </Link>
                  </li>
                )}
              </ul>
            </Accordion.Collapse>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Data Center"]?.read &&
            moduleOperations?.["Data Center"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-datacenter"
            bsPrefix={`nav-item ${active === "dataCenter" ? "active" : ""} `}
            onClick={() => {
              setActive("dataCenter");
              clearActiveStates();
            }}
          >
            <Link
              className={`${location.pathname === `/${ROUTES.DATA_CENTER.LIST}` ? "active" : ""} nav-link`}
              to={`/${ROUTES.DATA_CENTER.LIST}`}
            >
              <DataCenterIcon sx={{ fontSize: 22, color: "white" }} />
              <i className="sidenav-mini-icon"> U </i>
              <span className="item-name">Data Center</span>
            </Link>
          </Accordion.Item>
        )}

        {moduleOperations?.["SMS Webhook"]?.read &&
          moduleOperations?.["SMS Webhook"]?.view_permission && (
            <Accordion.Item
              as="li"
              eventKey="sidebar-sms-webhook"
              bsPrefix={`nav-item ${active === "sms-webhook" ? "active" : ""} `}
              onClick={() => {
                clearActiveStates(null, "sms-webhook");
                setActive("sms-webhook");
              }}
            >
              <ul className="sub-nav">
                <Link
                  className={`${location.pathname === `/${ROUTES.SMSWEBHOOK.LIST}` ? "active" : ""} nav-link`}
                  to={`/${ROUTES.SMSWEBHOOK.LIST}`}
                >
                  <SmsIcon sx={{ fontSize: 22, color: "white" }} />
                  <span className="item-name">Sms Webhooks</span>
                </Link>
              </ul>
            </Accordion.Item>
          )}

        {moduleOperations?.["Webhook"]?.read &&
          moduleOperations?.["Webhook"]?.view_permission && (
            <Accordion.Item
              as="li"
              eventKey="sidebar-manage-webhook"
              bsPrefix={`nav-item ${active === "manageWebhook" ? "active" : ""} `}
              onClick={() => {
                clearActiveStates(null, "manageWebhook");
                setActive("manageWebhook");
              }}
            >
              <ul className="sub-nav">
                <Link
                  className={`${location.pathname === `/${ROUTES.MANAGE_WEBHOOK.LIST}` ? "active" : ""} nav-link`}
                  to={`/${ROUTES.MANAGE_WEBHOOK.LIST}`}
                >
                  <SyncAltIcon sx={{ fontSize: 22, color: "white" }} />
                  <span className="item-name">Manage Webhooks</span>
                </Link>
              </ul>
            </Accordion.Item>
          )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Manage Prompts"]?.read &&
            moduleOperations?.["Manage Prompts"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-voice-library"
            bsPrefix={`nav-item ${active === "voiceLibrary" ? "active" : ""} `}
            onClick={() => {
              setActive("voiceLibrary");
              clearActiveStates();
            }}
          >
            <Link
              className={`${location.pathname === `/${ROUTES.VOICE_LIBRARY.MANAGE_PROMPTS}` ? "active" : ""} nav-link`}
              to={`/${ROUTES.VOICE_LIBRARY.MANAGE_PROMPTS}`}
            >
              <VoiceLibraryIcon sx={{ fontSize: 22, color: "white" }} />
              <span className="item-name">Manage Prompts</span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Gateway Management"]?.read &&
            moduleOperations?.["Gateway Management"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-servermanagement"
            bsPrefix={`nav-item ${active === "serverManagement" ? "active" : ""} `}
            onClick={() => {
              setActive("serverManagement");
              clearActiveStates();
            }}
          >
            <Link
              className={`${location.pathname === `/${ROUTES.SERVER_MANAGEMENT.LIST}` ? "active" : ""} nav-link`}
              to={`/${ROUTES.SERVER_MANAGEMENT.LIST}`}
            >
              <ServerManagementIcon sx={{ fontSize: 22, color: "white" }} />
              <i className="sidenav-mini-icon"> U </i>
              <span className="item-name">Gateway Management</span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Module List"]?.read &&
            moduleOperations?.["Module List"]?.view_permission) ||
          (moduleOperations?.["ACL Setting List"]?.read &&
            moduleOperations?.["ACL Setting List"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-module"
            bsPrefix={`nav-item ${active === "module" ? "active" : ""} `}
            onClick={() => {
              setActive("module");
              if (activeAccordionKey !== "sidebar-module") {
                setActiveAccordionKey("sidebar-module");
              }
            }}
          >
            <CustomToggle
              eventKey="sidebar-module"
              onClick={(activeKey) => {
                setActiveMenu(activeKey);
                if (activeAccordionKey !== activeKey.eventKey) {
                  setActiveAccordionKey(activeKey.eventKey);
                } else {
                  setActiveAccordionKey(null);
                }
              }}
              activeMenu={activeMenu}
            >
              <ModuleIcon sx={{ fontSize: 22, color: "white" }} />
              <span className="item-name">Module</span>
              <i className="right-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </i>
            </CustomToggle>
            <Accordion.Collapse eventKey="sidebar-module">
              <ul className="sub-nav">
                {(userRole === USER_ROLES.SUPERADMIN ||
                  (moduleOperations?.["Module List"]?.read &&
                    moduleOperations?.["Module List"]?.view_permission)) && (
                  <li className="nav-item">
                    <Link
                      className={`${location.pathname === `/${ROUTES.MODULE.LIST}` ? "active" : ""} nav-link`}
                      to={`/${ROUTES.MODULE.LIST}`}
                    >
                      <i className="icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          viewBox="0 0 24 24"
                          fill="white"
                        >
                          <g>
                            <circle cx="12" cy="12" r="8" fill="white"></circle>
                          </g>
                        </svg>
                      </i>
                      <i className="sidenav-mini-icon"> U </i>
                      <span className="item-name">Module List</span>
                    </Link>
                  </li>
                )}
                {(userRole === USER_ROLES.SUPERADMIN ||
                  (moduleOperations?.["ACL Setting List"]?.read &&
                    moduleOperations?.["ACL Setting List"]
                      ?.view_permission)) && (
                  <li className="nav-item">
                    <Link
                      className={`${location.pathname === `/${ROUTES.ACL_SETTING.LIST}` ? "active" : ""} nav-link`}
                      to={`/${ROUTES.ACL_SETTING.LIST}`}
                    >
                      <i className="icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          viewBox="0 0 24 24"
                          fill="white"
                        >
                          <g>
                            <circle cx="12" cy="12" r="8" fill="white"></circle>
                          </g>
                        </svg>
                      </i>
                      <i className="sidenav-mini-icon"> U </i>
                      <span className="item-name">ACL Setting List</span>
                    </Link>
                  </li>
                )}
              </ul>
            </Accordion.Collapse>
          </Accordion.Item>
        )}

        {((moduleOperations?.["Agents"]?.read &&
          moduleOperations?.["Agents"]?.view_permission) ||
          (moduleOperations?.["Call Group"]?.read &&
            moduleOperations?.["Call Group"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-manage-agents"
            bsPrefix={`nav-item ${active === "manageAgents" ? "active" : ""} `}
            onClick={() => {
              setActive("manageAgents");
              if (activeAccordionKey !== "sidebar-manage-agents") {
                setActiveAccordionKey("sidebar-manage-agents");
              }
            }}
          >
            <CustomToggle
              eventKey="sidebar-manage-agents"
              onClick={(activeKey) => {
                setActiveMenu(activeKey);
                if (activeAccordionKey !== activeKey.eventKey) {
                  setActiveAccordionKey(activeKey.eventKey);
                } else {
                  setActiveAccordionKey(null);
                }
              }}
              activeMenu={activeMenu}
            >
              <ManageAgentIcon sx={{ fontSize: 22, color: "white" }} />
              <span className="item-name">Manage Agents</span>
              <i className="right-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </i>
            </CustomToggle>
            <Accordion.Collapse eventKey="sidebar-manage-agents">
              <ul className="sub-nav">
                {moduleOperations?.["Agents"]?.read &&
                  moduleOperations?.["Agents"]?.view_permission && (
                    <li className="nav-item">
                      <Link
                        className={`${location.pathname === `/${ROUTES.MANAGE_AGENTS.AGENTS}` ? "active" : ""} nav-link`}
                        to={`/${ROUTES.MANAGE_AGENTS.AGENTS}`}
                      >
                        <i className="icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            viewBox="0 0 24 24"
                            fill="white"
                          >
                            <g>
                              <circle
                                cx="12"
                                cy="12"
                                r="8"
                                fill="white"
                              ></circle>
                            </g>
                          </svg>
                        </i>
                        <span className="item-name">Agents</span>
                      </Link>
                    </li>
                  )}
                {moduleOperations?.["Call Group"]?.read &&
                  moduleOperations?.["Call Group"]?.view_permission && (
                    <li className="nav-item">
                      <Link
                        className={`${location.pathname === `/${ROUTES.MANAGE_AGENTS.AGENT_GROUP_LIST}` ? "active" : ""} nav-link`}
                        to={`/${ROUTES.MANAGE_AGENTS.AGENT_GROUP_LIST}`}
                      >
                        <i className="icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            viewBox="0 0 24 24"
                            fill="white"
                          >
                            <g>
                              <circle
                                cx="12"
                                cy="12"
                                r="8"
                                fill="white"
                              ></circle>
                            </g>
                          </svg>
                        </i>
                        <span className="item-name">Call Group</span>
                      </Link>
                    </li>
                  )}
              </ul>
            </Accordion.Collapse>
          </Accordion.Item>
        )}

        {moduleOperations?.["Extension"]?.read &&
          moduleOperations?.["Extension"]?.view_permission && (
            <Accordion.Item
              as="li"
              eventKey="sidebar-extension"
              bsPrefix={`nav-item ${active === "extension" ? "active" : ""} `}
              onClick={() => {
                clearActiveStates(null, "extension");
                setActive("extension");
              }}
            >
              <Link
                className={`${location.pathname === `/${ROUTES.MANAGE_AGENTS.EXTENSION}` ? "active" : ""} nav-link`}
                to={`/${ROUTES.MANAGE_AGENTS.EXTENSION}`}
              >
                <ExtensionIcon sx={{ fontSize: 22, color: "white" }} />
                <span style={{ marginLeft: "15px" }} className="item-name">
                  Extension
                </span>
              </Link>
            </Accordion.Item>
          )}

        <Accordion.Item
          hidden={userRole !== USER_ROLES.SUPERADMIN}
          as="li"
          eventKey="sidebar-agent-file-list"
          bsPrefix={`nav-item ${active === "agent-file-list" ? "active" : ""} `}
          onClick={() => {
            setActive("agent-file-list");
            clearActiveStates();
          }}
        >
          <Link
            className={`${location.pathname === `/${ROUTES.MANAGE_AGENTS.AGENT_FILE_LIST}` ? "active" : ""} nav-link`}
            to={`/${ROUTES.MANAGE_AGENTS.AGENT_FILE_LIST}`}
          >
            <CallcenterListIcon sx={{ fontSize: 22, color: "white" }} />
            <span style={{ marginLeft: "15px" }} className="item-name">
              Agent file list
            </span>
          </Link>
        </Accordion.Item>

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Activity List"]?.read &&
            moduleOperations?.["Activity List"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-journeyoperation"
            bsPrefix={`nav-item ${active === "journeyOperation" ? "active" : ""} `}
            onClick={() => {
              setActive("journeyOperation");
              clearActiveStates();
            }}
          >
            <Link
              className={`${location.pathname === `/${ROUTES.JOURNEY_OPERATION.LIST}` ? "active" : ""} nav-link`}
              to={`/${ROUTES.JOURNEY_OPERATION.LIST}`}
            >
              <ActivityListIcon sx={{ fontSize: 22, color: "white" }} />
              <i className="sidenav-mini-icon"> U </i>
              <span className="item-name">Activity List</span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Voice Plans"]?.read &&
            moduleOperations?.["Voice Plans"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-voice-plans"
            bsPrefix={`nav-item ${active === "voice-plans" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "voice-plans");
              setActive("voice-plans");
            }}
          >
            <Link
              className={`${location.pathname === `/${ROUTES.VOICE_PLAN.LIST}` ? "active" : ""} nav-link`}
              to={`/${ROUTES.VOICE_PLAN.LIST}`}
            >
              <VoicePlanIcon sx={{ fontSize: 22, color: "white" }} />
              <span style={{ marginLeft: "15px" }} className="item-name">
                Voice Plans
              </span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Flash Message"]?.read &&
            moduleOperations?.["Flash Message"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-flash-message"
            bsPrefix={`nav-item ${active === "flash-message" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "flash-message");
              setActive("flash-message");
            }}
          >
            <Link
              className={`${location.pathname === `/${ROUTES.FLASH_MESSAGE.LIST}` ? "active" : ""} nav-link`}
              to={`/${ROUTES.FLASH_MESSAGE.LIST}`}
            >
              <MessageIcon sx={{ fontSize: 22, color: "white" }} />
              <span style={{ marginLeft: "15px" }} className="item-name">
                Flash Message
              </span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["TTS Plans"]?.read &&
            moduleOperations?.["TTS Plans"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-tts-plans"
            bsPrefix={`nav-item ${active === "tts_plans" ? "active" : ""} `}
            onClick={() => {
              setActive("tts_plans");
              clearActiveStates();
            }}
          >
            <Link
              className={`${location.pathname === `/${ROUTES.TTSPLANS.LIST}` ? "active" : ""} nav-link`}
              to={`/${ROUTES.TTSPLANS.LIST}`}
            >
              <VoicePlanIcon sx={{ fontSize: 22, color: "white" }} />
              <span style={{ marginLeft: "15px" }} className="item-name">
                TTS Plans
              </span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Realtime"]?.read &&
            moduleOperations?.["Realtime"]?.view_permission)) && (
          <Accordion.Item
            as="li"
            eventKey="sidebar-realtime"
            bsPrefix={`nav-item ${active === "realtime" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "realtime");
              setActive("realtime");
            }}
          >
            <Link
              className={`${location.pathname === `/${ROUTES.REALTIME.LIST}` ? "active" : ""} nav-link`}
              to={`/${ROUTES.REALTIME.LIST}`}
            >
              <RealtimeIcon sx={{ fontSize: 22, color: "white" }} />
              <span style={{ marginLeft: "15px" }} className="item-name">
                Realtime
              </span>
            </Link>
          </Accordion.Item>
        )}

        {(userRole === USER_ROLES.SUPERADMIN ||
          (moduleOperations?.["Remarks Status"]?.read &&
            moduleOperations?.["Remarks Status"]?.view_permission)) && (
          <Accordion.Item
            hidden={
              !(
                userRole === USER_ROLES.SUPERADMIN ||
                activeModule.includes("Remarks Status")
              )
            }
            as="li"
            eventKey="sidebar-remarks-status"
            bsPrefix={`nav-item ${active === "remarks-status" ? "active" : ""} `}
            onClick={() => {
              clearActiveStates(null, "remarks-status");
              setActive("remarks-status");
            }}
          >
            <Link
              className={`${location.pathname === `/${ROUTES.REMARKS.LIST}` ? "active" : ""} nav-link`}
              to={`/${ROUTES.REMARKS.LIST}`}
            >
              <RemarkIcon sx={{ fontSize: 22, color: "white" }} />
              <span style={{ marginLeft: "15px" }} className="item-name">
                Remarks Status
              </span>
            </Link>
          </Accordion.Item>
        )}

        <li>
          <hr className="hr-horizontal" />
        </li>
      </Accordion>
      <style>
        {`        
          .search-input::placeholder {
            color: #fff;
            opacity: 1;
          }`
        }
      </style>
    </Fragment>
  );
});

export default VerticalNavWithSidebar;
